import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Paper,
  Button
} from "@mui/material";

/* ================= OPTIONS ================= */

const operatorOptions = [
  ">", "<", "=", "+", "-", "*", "/", "%", "(", ")", "MAX", "MIN", "AVG"
];

const valueTypeOptions = [
  "Constant",
  "Variable",
  "Attributes",
  "Date",
  "Sequence Number"
];

const logicalOperators = ["AND", "OR", ")"];

/* ================= HELPERS ================= */

const createEmptyRow = (rowNo) => ({
  rowNo,
  operator: "",
  type: "",
  value: "",
  logicalOperator: ""
});

const isRowComplete = (row) =>
  row.operator && row.type && row.value;

/* ================= COMPONENT ================= */

const Rules = () => {
  const [ruleHeader, setRuleHeader] = useState({
    name: "",
    startDate: "",
    endDate: "",
    active: true
  });

  const [rows, setRows] = useState([createEmptyRow(1)]);

  /* ================= HEADER ================= */

  const handleHeaderChange = (key, value) => {
    setRuleHeader((prev) => ({ ...prev, [key]: value }));
  };

  /* ================= ROW CHANGE ================= */

  const handleRowChange = (idx, key, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [key]: value };

      // ✅ Auto add row AFTER VALUE entry
      if (
        key === "value" &&
        isRowComplete(updated[idx]) &&
        idx === updated.length - 1
      ) {
        updated.push(createEmptyRow(updated.length + 1));
      }

      return updated;
    });
  };

  /* ================= SAVE ================= */

  const buildRuleExpression = (rows) => {
  let currentVariable = "";

  return rows
    .map((row) => {
      let expr = "";

      // 1️⃣ Opening bracket + variable → STORE ONLY
      if (row.operator === "(") {
        if (row.type === "Variable" && row.value) {
          currentVariable = row.value;
          return "(";
        }
        return "(";
      }

      // 2️⃣ Normal condition row
      if (row.operator && row.value) {
        if (currentVariable) {
          expr = `${currentVariable} ${row.operator} ${row.value}`;
        } else {
          expr = `${row.operator} ${row.value}`;
        }
      }

      // 3️⃣ Logical operator / closing bracket
      if (row.logicalOperator === ")") {
        expr += " )";
      } else if (row.logicalOperator) {
        expr += ` ${row.logicalOperator}`;
      }

      return expr.trim();
    })
    .filter(Boolean)
    .join(" ");
};

  const handleSave = () => {
    // const validRows = rows.filter(isRowComplete);

    const payload = {
      ruleHeader,
      conditions: rows,
      expression: buildRuleExpression(rows)
    };

    console.log("🔹 RULE HEADER:", ruleHeader);
    console.log("🔹 RULE ROWS:", rows);
    console.log("🔥 RULE EXPRESSION:", payload.expression);

    alert("Rule saved – check console");
  };

  /* ================= CANCEL ================= */

  const handleCancel = () => {
    setRuleHeader({
      name: "",
      startDate: "",
      endDate: "",
      active: true
    });
    setRows([createEmptyRow(1)]);
  };

  /* ================= UI ================= */

  return (
    <Box>
      {/* ===== HEADER ===== */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6">Rule Details</Typography>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Name of Rule"
              value={ruleHeader.name}
              onChange={(e) =>
                handleHeaderChange("name", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={ruleHeader.startDate}
              onChange={(e) =>
                handleHeaderChange("startDate", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={ruleHeader.endDate}
              onChange={(e) =>
                handleHeaderChange("endDate", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={ruleHeader.active}
                  onChange={(e) =>
                    handleHeaderChange("active", e.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ===== RULE GRID ===== */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Rule Conditions</Typography>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Operator</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Condition</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.rowNo}</TableCell>

                <TableCell>
                  <Select
                    fullWidth
                    value={row.operator}
                    onChange={(e) =>
                      handleRowChange(idx, "operator", e.target.value)
                    }
                  >
                    {operatorOptions.map((op) => (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    fullWidth
                    value={row.type}
                    onChange={(e) =>
                      handleRowChange(idx, "type", e.target.value)
                    }
                  >
                    {valueTypeOptions.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <TextField
                    fullWidth
                    value={row.value}
                    onChange={(e) =>
                      handleRowChange(idx, "value", e.target.value)
                    }
                  />
                </TableCell>

                {/* ✅ CONDITION AVAILABLE FOR ALL ROWS */}
                <TableCell>
                  <Select
                    fullWidth
                    value={row.logicalOperator}
                    onChange={(e) =>
                      handleRowChange(
                        idx,
                        "logicalOperator",
                        e.target.value
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">None</MenuItem>
                    {logicalOperators.map((op) => (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* ===== ACTIONS ===== */}
        <Box mt={3} display="flex" gap={2}>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>

          <Button
            variant="outlined"
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              borderColor: "#000"
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Rules;
